import type { UserInputValidationError } from '@tectonics/prs/validation';
import type { IncomingPayment, OpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import { getSafePaymentOptionsV2 } from '@tectonics/prs/incoming-payment/get-payment-options';
import { getSafeEWalletPaymentOption } from '@tectonics/prs/incoming-payment/payment-option';
import { getSafeDirectDebitPaymentOption } from '@tectonics/prs/incoming-payment/payment-option/get-direct-debit-payment-option';

export const DIRECT_DEBIT = 'direct-debit';

type BankListReader = (paymentOptions: ReturnType<typeof getSafePaymentOptionsV2>) => readonly { readonly value: string }[];

interface BankBackedEWallet {
  readonly readBankList: BankListReader;
  readonly requiresBankList: boolean;
}

const BANK_BACKED_EWALLETS: { readonly [ewallet: string]: BankBackedEWallet } = {
  ussd: { readBankList: getSafeEWalletPaymentOption, requiresBankList: false },
  [DIRECT_DEBIT]: {
    readBankList: getSafeDirectDebitPaymentOption,
    requiresBankList: true,
  },
};

export function validateNigerianBankCode(
  selectedPaymentMethod: string,
  selectedEwallet: string,
  bankCodeValue: string,
  incomingPayment?: IncomingPayment | null
): UserInputValidationError | undefined {
  const ewallet = BANK_BACKED_EWALLETS[selectedEwallet];

  if (selectedPaymentMethod !== 'ewallet' || ewallet === undefined) {
    return undefined;
  }

  const bankCodes = getBankCodes(ewallet.readBankList, incomingPayment);

  if (bankCodes === null || bankCodes.length === 0) {
    if (ewallet.requiresBankList) {
      return { id: 'bank-list-unavailable' };
    }

    return isEmpty(bankCodeValue) ? { id: 'empty' } : undefined;
  }

  if (isEmpty(bankCodeValue)) {
    return { id: 'empty' };
  }

  return bankCodes.includes(bankCodeValue) ? undefined : { id: 'invalid' };
}

function isEmpty(bankCodeValue: string | null | undefined): boolean {
  return bankCodeValue === '' || bankCodeValue === null || bankCodeValue === undefined;
}

function getBankCodes(
  readBankList: BankListReader,
  incomingPayment?: IncomingPayment | null
): readonly string[] | null {
  if (!incomingPayment || incomingPayment.status !== 'OP') {
    return null;
  }

  return readBankCodes(readBankList, incomingPayment);
}

function readBankCodes(
  readBankList: BankListReader,
  incomingPayment: OpenedIncomingPayment
): readonly string[] | null {
  try {
    return readBankList(getSafePaymentOptionsV2(incomingPayment)).map(bank => bank.value);
  } catch {
    return null;
  }
}
